import {
    Alert,
    Box,
    Button,
    Card,
    Col,
    Form,
    FormButtonRow,
    FormSection,
    IconButton,
    Row,
    Text,
} from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { AddEdit } from '@common/utils/utilityTypes';
import type { ValidationErrors } from '@frontend/utils';
import { type EpisodeCategory, ManagerAuthorization } from '@common/enums';
import { getLanguage } from '@common/translate/translate';
import { isActiveOnDate } from '@common/utils/isActiveOnDate';
import {
    type CompensationPayoutForServiceActivity,
    type CompensationPayoutForServiceActivityUpdatableFields,
    compensationPayoutUpdateSchema,
    type CompensationPayoutWithId,
} from '@common/validation/compensationPayoutForServiceActivity';
import { validateDatetimeOverlap } from '@common/validation/utils/validateDatetimeOverlap';
import { Currency, DateInput, InputCurrency } from '@frontend/components/elements/common';
import { ConfirmationModal } from '@frontend/components/elements/modals/ConfirmationModal';
import { NullableFormattedDate } from '@frontend/components/elements/NullableFormattedDate';
import { CancelButton } from '@frontend/components/userDataManagement/CancelButton';
import { SaveButton } from '@frontend/components/userDataManagement/SaveButton';
import { getServiceActivityCompensationPayouts, updateAll } from '@frontend/services/api/compensationPayoutApiService';
import {
    emptyValidationErrors,
    hasOneAuthorization,
    updateValidationErrorsForMany,
    updateValidationErrorsForOne,
    validate,
} from '@frontend/utils';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { isConfirmationRequired } from './helpers';
import { getBadge, getCompensationEndDate, getOverlappingMessage } from './sharedComponents';

type ServiceActivityCompensationPayoutProps = {
    canEdit: boolean;
    compensationPayouts: CompensationPayoutForServiceActivity[];
    episodeCategory: EpisodeCategory;
    higherLevelCompensationPayout?: CompensationPayoutForServiceActivity;
    onSubmit: () => void;
    organizationId: string;
    rightEndDate?: string | Date;
    rightStartDate: string | Date;
    serviceActivityId: string;
    serviceEmploymentId?: string;
    serviceId: string;
    serviceOrganizationSettingId?: string;
    title: string;
};

export type CompensationPayoutAddEdit = AddEdit<CompensationPayoutForServiceActivityUpdatableFields> &
    Pick<CompensationPayoutForServiceActivity, 'serviceActivityId'> & { errors: ValidationErrors; id?: string };

export function ServiceActivityCompensationPayout(props: Readonly<ServiceActivityCompensationPayoutProps>) {
    const {
        canEdit,
        compensationPayouts,
        episodeCategory,
        higherLevelCompensationPayout,
        onSubmit,
        organizationId,
        rightEndDate,
        rightStartDate,
        title,
        serviceActivityId,
        serviceId,
        serviceEmploymentId,
        serviceOrganizationSettingId,
    } = props;
    const translate = getLanguage();

    const abortSignal = useAbortSignal();
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [updatedCompensationPayouts, setUpdatedCompensationPayouts] = useState<CompensationPayoutAddEdit[]>(
        getInitialCompensationPayoutsAddEdit(compensationPayouts)
    );
    const [serviceLevelCompensationPayouts, setServiceLevelCompensationPayouts] = useState<
        CompensationPayoutForServiceActivity[]
    >([]);

    useEffect(() => {
        loadServiceActivityCompensationPayouts();
    }, []);

    return (
        <Card>
            <Card.Transition type={isEditing ? 'edit' : 'read'} />
            {canEdit ? (
                <Card.Header
                    action={
                        !isEditing ? (
                            <IconButton
                                icon="edit-light"
                                labelText="edit"
                                onClick={() => setIsEditing(true)}
                            />
                        ) : undefined
                    }
                    title={title}
                />
            ) : (
                <Card.Header title={title} />
            )}
            <Card.Body>
                <Form submitOnEnter={false}>
                    {getOverlappingMessage(higherLevelCompensationPayout, getLink(higherLevelCompensationPayout))}
                    {getServiceSettingMessage(higherLevelCompensationPayout)}
                    <FormSection>
                        <Row
                            columnGap={4}
                            rowGap={4}
                        >
                            <Col span={[12, 3, 2]}>{translate.compensationPayouts.fees}</Col>
                            <Col span={[12, 9, 10]}>{translate.common.startDateAndEndDate} </Col>
                        </Row>
                    </FormSection>
                    {updatedCompensationPayouts.map((compensationPayout, index) => {
                        return (
                            <FormSection key={index}>
                                <Row
                                    columnGap={4}
                                    rowGap={4}
                                >
                                    {!isEditing && (
                                        <>
                                            <Col span={[12, 4, 2]}>
                                                <Text
                                                    as="label"
                                                    fontWeight="medium"
                                                >
                                                    <Currency value={compensationPayout.amount} />
                                                </Text>
                                            </Col>
                                            <Col span={[12, 8, 6]}>
                                                <Text
                                                    as="label"
                                                    fontWeight="medium"
                                                >
                                                    <NullableFormattedDate value={compensationPayout.startDate} />
                                                </Text>
                                                <Text
                                                    as="label"
                                                    mx={4}
                                                    size="md"
                                                >
                                                    {translate.common.until}
                                                </Text>
                                                <Text
                                                    as="label"
                                                    fontWeight="medium"
                                                >
                                                    {getCompensationEndDate(compensationPayout.endDate, rightEndDate)}
                                                </Text>
                                            </Col>
                                            <Col span={[12, 4]}>
                                                {getBadge({
                                                    compensationPayout:
                                                        compensationPayout as CompensationPayoutForServiceActivity,
                                                    higherLevelCompensationPayout,
                                                    rightStartDate,
                                                    rightEndDate,
                                                })}
                                            </Col>
                                        </>
                                    )}
                                    {isEditing && (
                                        <>
                                            <Col span={[12, 3, 2]}>
                                                <Box display="flex">
                                                    <Text
                                                        as="label"
                                                        mr={2.5}
                                                        pt={2.5}
                                                        size="xs"
                                                    >
                                                        €
                                                    </Text>
                                                    <InputCurrency
                                                        error={compensationPayout.errors.get('amount')}
                                                        onChange={(value) => handleChange('amount', value, index)}
                                                        value={compensationPayout.amount}
                                                    />
                                                </Box>
                                            </Col>
                                            <Col span={[12, 8, 6]}>
                                                <Box
                                                    display="flex"
                                                    flexDirection={['column', 'row']}
                                                    gap={2}
                                                >
                                                    <Box>
                                                        <DateInput
                                                            error={compensationPayout.errors.get('startDate')}
                                                            maxDate={rightEndDate as string}
                                                            minDate={rightStartDate as string}
                                                            onChange={(value) =>
                                                                handleChange('startDate', value, index)
                                                            }
                                                            placeholder={translate.common.dateFormat}
                                                            showOutOfBoundErrorsValidation={true}
                                                            value={compensationPayout.startDate as string}
                                                            variant="outline"
                                                        />
                                                    </Box>
                                                    <Text
                                                        as="label"
                                                        pt={2.5}
                                                        size="xs"
                                                    >
                                                        {translate.common.until}
                                                    </Text>
                                                    <Box>
                                                        <DateInput
                                                            error={compensationPayout.errors.get('endDate')}
                                                            maxDate={rightEndDate as string}
                                                            minDate={rightStartDate as string}
                                                            onChange={(value) => handleChange('endDate', value, index)}
                                                            placeholder={translate.common.dateFormat}
                                                            showOutOfBoundErrorsValidation={true}
                                                            value={compensationPayout.endDate as string}
                                                            variant="outline"
                                                        />
                                                    </Box>
                                                </Box>
                                            </Col>
                                            <Col span={[12, 1, 4]}>
                                                <Box
                                                    display="flex"
                                                    justifyContent={['center', 'flex-end']}
                                                >
                                                    <IconButton
                                                        icon="delete"
                                                        onClick={() => onRemoveCompensation(index)}
                                                    />
                                                </Box>
                                            </Col>
                                        </>
                                    )}
                                </Row>
                            </FormSection>
                        );
                    })}
                    {isEditing && (
                        <>
                            <Button
                                onClick={() => onAddCompensation()}
                                style={{ marginTop: '32px' }}
                                variant="link"
                            >
                                {translate.compensationPayouts.addFees}
                            </Button>

                            <FormButtonRow>
                                <CancelButton
                                    disabled={isSaving}
                                    onCancel={async () => handleCancel()}
                                />
                                <SaveButton
                                    disabled={isSaving}
                                    onSave={async () => {
                                        const { hasErrors, validatedCompensationPayouts } =
                                            validateCompensationPayouts(updatedCompensationPayouts);

                                        if (hasErrors) {
                                            setUpdatedCompensationPayouts(validatedCompensationPayouts);

                                            return;
                                        }

                                        isConfirmationRequired(updatedCompensationPayouts, compensationPayouts)
                                            ? setShowConfirmationModal(true)
                                            : await handleSubmit();
                                    }}
                                />
                            </FormButtonRow>
                        </>
                    )}
                    <ConfirmationModal
                        cancelText={translate.common.cancelling}
                        confirmText={translate.common.proceed}
                        handleNo={() => setShowConfirmationModal(false)}
                        handleYes={() => {
                            setShowConfirmationModal(false);
                            handleSubmit();
                        }}
                        isOpen={showConfirmationModal}
                        question="Deze wijziging heeft financiële gevolgen op reeds uitbetaalde processen. Neem contact op met Finance."
                        title="Financiële gevolgen ingestelde datum"
                    />
                </Form>
            </Card.Body>
        </Card>
    );

    async function loadServiceActivityCompensationPayouts() {
        const response = await getServiceActivityCompensationPayouts({ serviceActivityId, serviceId }, abortSignal);
        setServiceLevelCompensationPayouts(response);
    }

    function getServiceSettingMessage(higherLevelCompensationPayout?: CompensationPayoutForServiceActivity) {
        const activeServiceLevelCompensationPayout = serviceLevelCompensationPayouts.find(
            (serviceLevelCompensationPayout) => {
                return isActiveOnDate(serviceLevelCompensationPayout, new Date());
            }
        );
        const activeCompensationPayout = compensationPayouts.find(
            (compensationPayout) =>
                isActiveOnDate(compensationPayout, new Date()) &&
                isActiveOnDate({ rightEndDate, startDate: rightStartDate || null }, new Date())
        );

        if (!activeServiceLevelCompensationPayout || activeCompensationPayout || higherLevelCompensationPayout) {
            return;
        }

        return (
            <Alert appearance="info">
                Er is geen vergoeding ingevuld, daarom geldt{' '}
                <Currency value={activeServiceLevelCompensationPayout.amount} /> als standaardprijs (
                {hasOneAuthorization(ManagerAuthorization.servicesRead) ? (
                    <Alert.Link
                        to={getLink(activeServiceLevelCompensationPayout)}
                        variant="link"
                    >
                        dienstniveau
                    </Alert.Link>
                ) : (
                    'dienstniveau'
                )}
                ).
            </Alert>
        );
    }

    function getLink(higherLevelCompensationPayout?: CompensationPayoutForServiceActivity) {
        if (!higherLevelCompensationPayout) {
            return '';
        }
        let link = `/serviceActivityCompensationPayout/${higherLevelCompensationPayout.id}?`;
        if (higherLevelCompensationPayout.serviceGroupSettingId) {
            link += `&serviceGroupSettingId=${higherLevelCompensationPayout.serviceGroupSettingId}&serviceId=${serviceId}`;
        } else if (higherLevelCompensationPayout.serviceOrganizationSettingId) {
            link = `/organizations/${organizationId}/view/compensationPayouts/${episodeCategory}`;
        } else if (higherLevelCompensationPayout.serviceId) {
            link = `/services/${higherLevelCompensationPayout.serviceId}?tab=serviceCompensationPayouts`;
        }

        return link;
    }

    function handleCancel() {
        setIsEditing(false);
        setUpdatedCompensationPayouts(getInitialCompensationPayoutsAddEdit(compensationPayouts));
    }

    function getInitialCompensationPayoutsAddEdit(
        compensationPayoutForServiceActivities: CompensationPayoutForServiceActivity[]
    ) {
        return compensationPayoutForServiceActivities.map((compensationPayout) => ({
            ...compensationPayout,
            errors: emptyValidationErrors,
        }));
    }

    async function handleSubmit() {
        setIsSaving(true);

        const validateCompensationPayoutsResult = validateCompensationPayouts(updatedCompensationPayouts);

        if (!validateCompensationPayoutsResult.hasErrors) {
            const compensationPayoutsWithEntity = validateCompensationPayoutsResult.validatedCompensationPayouts.map(
                (compensationPayout) => ({
                    compensationPayout: {
                        amount: compensationPayout.amount,
                        endDate: compensationPayout.endDate,
                        serviceActivityId,
                        startDate: compensationPayout.startDate,
                        ...(serviceEmploymentId ? { serviceEmploymentId } : {}),
                        ...(serviceOrganizationSettingId ? { serviceOrganizationSettingId } : {}),
                        serviceGroupSettingId: undefined,
                        serviceId: undefined,
                    },
                    id: compensationPayout.id,
                })
            ) as CompensationPayoutWithId[];

            await updateAll(
                {
                    compensationPayouts: compensationPayoutsWithEntity,
                    serviceActivityId,
                    serviceEmploymentId,
                    serviceOrganizationSettingId,
                },
                abortSignal
            );

            setIsEditing(false);
            onSubmit();
        }
        setIsSaving(false);
    }

    function handleChange<K extends keyof CompensationPayoutAddEdit>(
        name: K,
        value: CompensationPayoutAddEdit[K],
        index: number
    ) {
        const changedCompensationPayouts = [...updatedCompensationPayouts];
        const updatedCompensation = {
            ...changedCompensationPayouts[index],
            [name]: value,
        };

        if (name === 'startDate') {
            updatedCompensation.errors = updateValidationErrorsForMany(
                compensationPayoutUpdateSchema(rightStartDate, rightEndDate),
                updatedCompensation,
                updatedCompensation.errors,
                ['startDate', 'endDate']
            );
        } else {
            updatedCompensation.errors = updateValidationErrorsForOne(
                compensationPayoutUpdateSchema(rightStartDate, rightEndDate),
                updatedCompensation,
                updatedCompensation.errors,
                name
            );
        }
        changedCompensationPayouts[index] = updatedCompensation;
        setUpdatedCompensationPayouts(changedCompensationPayouts);
    }

    function onRemoveCompensation(index: number) {
        const removedCompensationPayouts = [...updatedCompensationPayouts];
        removedCompensationPayouts.splice(index, 1);
        setUpdatedCompensationPayouts(removedCompensationPayouts);
    }

    function onAddCompensation() {
        setUpdatedCompensationPayouts([...updatedCompensationPayouts, createNewCompensationPayout()]);
    }

    function createNewCompensationPayout(): CompensationPayoutAddEdit {
        return {
            amount: null,
            endDate: null,
            errors: emptyValidationErrors,
            serviceActivityId: serviceActivityId,
            startDate: null,
        };
    }

    function validateCompensationPayouts(compensations: CompensationPayoutAddEdit[]) {
        let hasErrors = false;
        let validatedCompensationPayouts = [...compensations];

        for (const compensation of validatedCompensationPayouts) {
            compensation.errors = emptyValidationErrors;
            const result = validate(compensationPayoutUpdateSchema(rightStartDate, rightEndDate), compensation);
            if (result.hasErrors) {
                hasErrors = true;
                compensation.errors = result.errors;
            }
        }

        if (!hasErrors) {
            validatedCompensationPayouts = validateDatetimeOverlap(compensations);
        }

        hasErrors = validatedCompensationPayouts.some((compensation) =>
            compensation.errors.some((error) => error !== undefined)
        );

        return { hasErrors, validatedCompensationPayouts };
    }
}
